import type { AuthData, DeviceData } from "../../types";
import type { CrossmintApiService } from "../api";
import { CrossmintHttpError } from "../api/request";
import type { FrameLogger } from "../logger";
import { CrossmintFrameService } from "../service";

interface DeviceCacheEntry extends DeviceData {
	timestamp: number;
}

/**
 * In-memory cache for device data containing encrypted master secrets.
 *
 * **IMPORTANT: This is an in-memory cache that lives within the iframe context.**
 *
 * The cache is automatically cleared when:
 * - **Page refresh/reload** - Browser discards the iframe's JavaScript context
 * - **Tab close** - Browser terminates the iframe along with its memory
 * - **Browser navigation away** - Parent page navigation destroys the iframe
 * - **Iframe removal** - Parent page removes the iframe from DOM
 * - **Browser crash/restart** - All memory is lost
 * - **Manual cache clear** - Explicit call to `clearCache()` method
 *
 * The cache uses a 5-minute TTL (time-to-live) to automatically expire entries
 * and ensure encrypted master secrets don't remain in memory indefinitely. Each cache entry
 * is isolated by device ID, API key, JWT and recovery method to prevent cross-contamination
 * between different authentication contexts.
 *
 * Since this cache exists in iframe memory, it provides no persistence across
 * browser sessions and is automatically cleaned up when the iframe context ends.
 */
export class DeviceCache extends CrossmintFrameService {
	name = "Device Cache";
	log_prefix = "[DeviceCache]";

	private deviceCache = new Map<string, DeviceCacheEntry>();
	private cleanupInterval: ReturnType<typeof setInterval> | null = null;

	constructor(
		private readonly api: CrossmintApiService,
		protected readonly logger: FrameLogger,
		private readonly CACHE_TTL_MS = 5 * 60 * 1000,
	) {
		super(logger);
	}

	async init() {
		if (this.cleanupInterval != null) {
			clearInterval(this.cleanupInterval);
		}
		this.cleanupInterval = setInterval(() => {
			this.cleanupStaleEntries();
		}, this.CACHE_TTL_MS);

		this.logger.debug(
			this.log_prefix,
			"Started periodic cache cleanup interval",
		);
	}

	/**
	 * Retrieves device data containing encrypted master secrets with intelligent caching for performance optimization.
	 *
	 * This method implements a cache-first strategy to minimize API calls while maintaining security:
	 * 1. **Cache Check**: First checks if a valid cached entry exists for the credential combination
	 * 2. **TTL Validation**: Ensures cached entries haven't exceeded the 5-minute time-to-live
	 * 3. **API Fallback**: Fetches from Crossmint API if cache miss or expired entry
	 * 4. **Automatic Caching**: Stores successful API responses for future requests
	 *
	 * Cache isolation is maintained through a composite key that includes device ID, API key,
	 * JWT and recovery method, ensuring that different users, applications, authentication
	 * contexts or recovery methods cannot access each other's cached device data.
	 *
	 * The method gracefully handles missing device data by returning null (e.g., when
	 * a device hasn't been properly enrolled), while propagating other API errors for proper
	 * error handling by the calling code.
	 *
	 * @param deviceId - Unique device identifier for cache isolation
	 * @param authData - Authentication credentials for API access
	 * @param authData.jwt - JSON Web Token for user authentication
	 * @param authData.apiKey - API key for application authentication
	 * @param authId - Recovery method whose record is wanted; omitted by SDKs that predate it
	 * @returns Promise resolving to device data if available, null if not found (404)
	 * @throws {Error} For API errors other than 404 (network issues, invalid credentials, etc.)
	 */
	public async get(
		deviceId: string,
		authData: AuthData,
		authId?: string,
	): Promise<DeviceData | null> {
		this.logger.info(this.log_prefix, `Getting device data`);
		const cacheKey = this.buildCacheKey(deviceId, authData, authId);

		const cached = this.deviceCache.get(cacheKey);
		if (cached != null && this.isCacheEntryValid(cached)) {
			this.logger.debug(this.log_prefix, "Using cached device data");
			return this.toDeviceData(cached);
		}

		try {
			this.logger.info(this.log_prefix, "Fetching device data from Relay");
			const result = await this.api.getDevice(
				deviceId,
				undefined,
				authData,
				authId,
			);
			const data = this.toDeviceData(result);
			this.deviceCache.set(cacheKey, { ...data, timestamp: Date.now() });

			this.logger.info(this.log_prefix, "Device data fetched from Relay");
			return data;
		} catch (e) {
			// Relay will sometimes return 304 if the response was previously 404
			// and has not changed. It should be treated the same as 404
			if (
				e instanceof CrossmintHttpError &&
				(e.status === 404 || e.status === 304)
			) {
				this.logger.info(
					this.log_prefix,
					`Device data not available from Relay`,
				);
				return null;
			}

			this.logger.error(this.log_prefix, `Error getting device data: ${e}`);
			throw e;
		}
	}

	/**
	 * Caches a freshly onboarded record under the recovery method it was onboarded for, so the
	 * next `get` for that method is served without a round trip.
	 */
	public set(
		deviceId: string,
		data: DeviceData,
		authData: AuthData,
		authId?: string,
	) {
		this.logger.info(this.log_prefix, `Caching entry`);
		this.deviceCache.set(this.buildCacheKey(deviceId, authData, authId), {
			...data,
			timestamp: Date.now(),
		});
	}

	public clearCache(): void {
		this.deviceCache.clear();
	}

	public cleanupStaleEntries(): void {
		const now = Date.now();
		this.logger.info(this.log_prefix, `Clearing stale cache entries`);

		this.deviceCache.forEach((entry, key) => {
			if (now - entry.timestamp >= this.CACHE_TTL_MS) {
				this.deviceCache.delete(key);
				this.logger.debug(this.log_prefix, `Cleared stale cache entry: ${key}`);
			}
		});
	}

	private toDeviceData(entry: DeviceData): DeviceData {
		return {
			encryptedMasterSecret: entry.encryptedMasterSecret,
			signerId: entry.signerId,
		};
	}

	private buildCacheKey(
		deviceId: string,
		authData: AuthData,
		authId: string | undefined,
	): string {
		const base = `${deviceId}-${authData.apiKey}-${authData.jwt}`;
		return authId != null ? `${base}-${authId}` : base;
	}

	private isCacheEntryValid(cached: DeviceCacheEntry): boolean {
		return Date.now() - cached.timestamp < this.CACHE_TTL_MS;
	}

	public safeDebugState(): Record<string, unknown> {
		const cacheEntries: Record<string, boolean> = {};

		this.deviceCache.forEach((entry, key) => {
			const keyParts = key.split("-");
			if (keyParts.length >= 3) {
				const safeKey = `${keyParts[0]}-${keyParts[1]}-...`;
				cacheEntries[safeKey] = this.isCacheEntryValid(entry);
			}
		});

		return {
			description:
				"Map of cache keys to validity status. true = valid cache entry, false = expired entry. Keys format: 'deviceId-clientApiKey-...' where public facing client API key is shown but JWT is hidden",
			cacheEntries,
		};
	}
}
